if (!customElements.get('book-preview')) {
  customElements.define(
    'book-preview',
    class BookPreview extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector('[data-book]');
        this.pagesSource = this.querySelector('[data-pages]');
        this.counterCurrent = this.querySelector('[data-counter-current]');
        this.counterPos = this.querySelector('[data-counter-pos]');
        this.counterPages = this.querySelector('[data-counter-pages]');
        this.labelEl = this.querySelector('[data-label]');
        this.prevButton = this.querySelector('[name="previous"]');
        this.nextButton = this.querySelector('[name="next"]');
        this.dots = Array.from(this.querySelectorAll('[data-dot]'));
        if (!this.pages || this.pages.length === 0) {
          // ponytail: kept across reconnects - PageFlip.destroy() removes the pages
          // from the DOM, so a re-inserted element re-adopts the detached nodes
          this.pages = Array.from(this.querySelectorAll('.x-book-preview__page'));
        }

        if (!this.stage || !window.St || this.pages.length === 0) return;

        // ISO 216 page ratio, used only until a spread image is uploaded
        this.aspect = parseFloat(this.dataset.aspect) || 0.7071;
        this.widthCap = parseInt(this.dataset.pageMax, 10) || screen.width;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.classList.add('is-initialized');
        this.initBook(this.readOrientation(), 0);

        // re-create the book only when CSS flips --pages-shown; within a mode,
        // stretch mode follows the container by itself
        this.modeObserver = new ResizeObserver(() => {
          if (!this.pageFlip) return;
          const portrait = this.readOrientation();
          if (portrait === this.portrait) return;
          const current = this.pageFlip.getCurrentPageIndex();
          try {
            this.pageFlip.destroy();
          } catch (error) {
            // ponytail: PageFlip.destroy throws if the DOM is already gone
          }
          this.initBook(portrait, current);
        });
        this.modeObserver.observe(this);

        this.prevButton?.addEventListener('click', () => this.pageFlip.flipPrev());
        this.nextButton?.addEventListener('click', () => this.pageFlip.flipNext());
        this.dots.forEach((dot, index) => {
          dot.addEventListener('click', () => this.pageFlip.flip(index * 2));
        });

        this.fullscreenButton = this.querySelector('[data-fullscreen]');
        this.fullscreenButton?.addEventListener('click', () => this.toggleFullscreen());
        this.addEventListener('fullscreenchange', () => {
          this.setFullscreenState(document.fullscreenElement === this);
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.pageFlip.flipPrev();
          if (event.key === 'ArrowRight') this.pageFlip.flipNext();
          if (event.key === 'Escape' && this.pseudoFullscreen) this.setFullscreenState(false);
        });

        if (window.Shopify && window.Shopify.designMode) {
          this.onBlockSelect = (event) => {
            const index = this.pages.indexOf(event.target);
            if (index >= 0) this.pageFlip.turnToPage(index);
          };
          document.addEventListener('shopify:block:select', this.onBlockSelect);
        }
      }

      readOrientation() {
        return getComputedStyle(this).getPropertyValue('--pages-shown').trim() === '1';
      }

      initBook(portrait, startPage) {
        this.portrait = portrait;
        if (!this.stage.isConnected) {
          // PageFlip.destroy() removes its mount element from the DOM
          this.stage = document.createElement('div');
          this.stage.className = 'x-book-preview__book';
          this.stage.setAttribute('data-book', '');
          this.querySelector('.x-book-preview__book-wrapper').prepend(this.stage);
        }
        this.pageFlip = new St.PageFlip(this.stage, {
          width: this.widthCap,
          height: Math.round(this.widthCap / this.aspect),
          size: 'stretch',
          // usePortrait: false pins landscape; minWidth = widthCap keeps the
          // portrait threshold (2x minWidth) above any possible container
          usePortrait: portrait,
          minWidth: portrait ? this.widthCap : 0,
          maxWidth: this.widthCap,
          minHeight: 0,
          maxHeight: Math.round(this.widthCap / this.aspect),
          maxShadowOpacity: 0.35,
          flippingTime: this.reducedMotion ? 100 : 700,
          showCover: false,
        });
        this.pageFlip.loadFromHTML(this.pages);
        this.pageFlip.on('flip', (event) => this.updateUI(event.data));
        if (startPage > 0) this.pageFlip.turnToPage(startPage);
        this.updateUI(startPage);
      }

      disconnectedCallback() {
        if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.modeObserver?.disconnect();
        try {
          this.pageFlip?.destroy();
        } catch (error) {
          // ponytail: PageFlip.destroy throws if the DOM is already gone (theme editor reloads)
        }
        this.pageFlip = null;
      }

      preloadSpread(spread) {
        // flipping lazy to eager fetches the hidden imgs immediately
        for (const page of this.pages.slice(spread * 2, spread * 2 + 2)) {
          page.querySelectorAll('img[loading="lazy"]').forEach((img) => {
            img.loading = 'eager';
          });
        }
      }

      updateUI(pageIndex) {
        const spread = Math.min(Math.floor(pageIndex / 2), this.dots.length - 1);
        const dot = this.dots[spread];
        this.preloadSpread(spread + 1);

        if (this.counterCurrent) this.counterCurrent.textContent = String(spread + 1).padStart(2, '0');
        if (this.labelEl) this.labelEl.textContent = dot?.dataset.title || '';

        const pagesText = dot?.dataset.pages || '';
        if (this.counterPages) {
          this.counterPages.textContent = pagesText;
          this.counterPages.hidden = !pagesText;
        }
        if (this.counterPos) this.counterPos.hidden = !!pagesText;

        const count = this.pageFlip.getPageCount();
        const lastIndex = this.pageFlip.getOrientation() === 'portrait' ? count - 1 : count - 2;
        this.prevButton?.classList.toggle('is-hidden', pageIndex <= 0);
        this.nextButton?.classList.toggle('is-hidden', pageIndex >= lastIndex);

        this.dots.forEach((d, i) => {
          d.classList.toggle('is-active', i === spread);
          if (i === spread) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      }

      toggleFullscreen() {
        if (document.fullscreenElement === this) {
          document.exitFullscreen();
        } else if (this.pseudoFullscreen) {
          this.setFullscreenState(false);
        } else if (this.requestFullscreen) {
          this.requestFullscreen().catch(() => this.setFullscreenState(true, true));
        } else {
          this.setFullscreenState(true, true);
        }
      }

      setFullscreenState(active, pseudo = false) {
        this.pseudoFullscreen = active && pseudo;
        this.classList.toggle('is-fullscreen', active);
        document.documentElement.style.overflow = this.pseudoFullscreen ? 'hidden' : '';
        const button = this.fullscreenButton;
        if (button) {
          const label = active ? button.dataset.labelClose : button.dataset.labelOpen;
          if (label) button.setAttribute('aria-label', label);
          if (this.pseudoFullscreen) button.focus();
        }
        requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
      }
    }
  );
}
